// src/pages/public/Maps.tsx
// Public venue maps page — overview, golf clubhouse, and grounds/parking maps.
// Moved off the Schedule page (issue #7). Gated on the event's showMap flag:
// shows a "coming soon" state when off, mirroring Schedule.tsx.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EventMaps from "../../components/maps/EventMaps";

interface EventDoc {
  showMap?: boolean;
}

export default function Maps() {
  const [event, setEvent] = useState<EventDoc | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/public/event?year=2026")
      .then(r => (r.ok ? r.json() : null))
      .then((d: EventDoc | null) => { setEvent(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Maps default off (showMap !== true) — show a friendly placeholder until an
  // admin turns them on in the Event Editor.
  if (!loading && event && event.showMap !== true) {
    return (
      <section className="section">
        <div className="container disabled-page">
          <span className="eyebrow">Coming Soon</span>
          <h1>Maps aren't posted yet</h1>
          <p>Venue and parking maps will be here closer to the event.</p>
          <Link to="/" className="btn-back">Back to home</Link>
          <style>{`
            .disabled-page { max-width: 640px; padding: 4rem 0; }
            .disabled-page h1 { font-family: var(--font-display); font-weight: 700; text-transform: uppercase; margin: 0.5rem 0 1rem; }
            .disabled-page p { color: var(--steel); margin-bottom: 2rem; font-size: 1.05rem; }
            .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); }
            .btn-back {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--white); background: var(--red); padding: 0.85rem 2rem;
              text-decoration: none;
            }
          `}</style>
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{`
        .maps-page { min-height: 100vh; background: var(--bg); }
        .maps-hero {
          background: var(--black); color: var(--white);
          padding: 5rem 4rem 4rem; position: relative; overflow: hidden;
        }
        .maps-hero::before {
          content: ''; position: absolute; inset: 0;
          background: repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(255,255,255,0.02) 12px, rgba(255,255,255,0.02) 24px);
          pointer-events: none;
        }
        .hero-inner { max-width: 1100px; margin: 0 auto; position: relative; }
        .eyebrow {
          font-family: var(--font-display); font-size: 0.72rem; font-weight: 500;
          letter-spacing: 0.35em; text-transform: uppercase; color: var(--red);
          margin-bottom: 0.75rem;
        }
        .maps-hero h1 {
          font-family: var(--font-display); font-size: clamp(2.4rem, 5vw, 3.6rem);
          font-weight: 700; text-transform: uppercase; line-height: 1; margin: 0;
        }
        .hero-desc {
          font-family: var(--font-body); font-size: 1.05rem; color: rgba(255,255,255,0.75);
          margin-top: 1.5rem; max-width: 60ch; line-height: 1.6;
        }
        .maps-body { max-width: 1100px; margin: 0 auto; padding: 4rem; }
        @media (max-width: 900px) {
          .maps-body { padding: 3rem 1.5rem; }
          .maps-hero { padding: 3.5rem 1.5rem 3rem; }
        }
        .state-box { padding: 4rem 2rem; text-align: center; font-family: var(--font-body); color: var(--muted); }
        .state-box h2 { font-family: var(--font-display); font-size: 1.4rem; font-weight: 600; text-transform: uppercase; color: var(--mid); }
      `}</style>

      <div className="maps-page">
        <div className="maps-hero">
          <div className="hero-inner">
            <p className="eyebrow">Getting Around</p>
            <h1>Maps &amp; Directions</h1>
            <p className="hero-desc">
              Where to go and where to park — the road overview, the golf clubhouse, and the
              picnic grounds at Riverdale Regional Park. Tap a numbered pin for details.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="state-box"><h2>Loading maps…</h2></div>
        ) : (
          <div className="maps-body">
            <EventMaps />
          </div>
        )}
      </div>
    </>
  );
}
